import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import Editor from "@monaco-editor/react";
import { useAuth } from "../context/AuthContext";
import { useConnection } from "../context/ConnectionContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "../components/ui/resizable";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Play,
  Database,
  Server,
  Loader2,
  Save,
  Download,
  Clock,
  AlertCircle,
  CheckCircle2,
  Copy,
  Info,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Edit2,
  Check,
  X,
  Search,
  ChevronDown,
  FileCode,
  Plus,
  Sparkles,
  ArrowRightLeft,
  Bookmark,
  Crown,
  Zap,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const ROWS_PER_PAGE = 20;

const SQL_TEMPLATES = {
  "SELECT All": "SELECT * FROM [TableName];",
  "SELECT Top": "SELECT TOP 100 * FROM [TableName];",
  "SELECT Where": "SELECT * FROM [TableName]\nWHERE [column] = 'value';",
  "SELECT Like": "SELECT * FROM [TableName]\nWHERE [column] LIKE '%search%';",
  "COUNT": "SELECT COUNT(*) AS total FROM [TableName];",
  "GROUP BY": "SELECT [column], COUNT(*) AS count\nFROM [TableName]\nGROUP BY [column]\nORDER BY count DESC;",
  "INNER JOIN": "SELECT a.*, b.*\nFROM [Table1] a\nINNER JOIN [Table2] b ON a.[id] = b.[foreign_id];",
  "INSERT": "INSERT INTO [TableName] ([column1], [column2])\nVALUES ('value1', 'value2');",
  "UPDATE": "UPDATE [TableName]\nSET [column1] = 'new_value'\nWHERE [column2] = 'value';",
  "DELETE": "DELETE FROM [TableName]\nWHERE [column] = 'value';",
  "CTE": "WITH CTE AS (\n  SELECT [column], ROW_NUMBER() OVER (PARTITION BY [group_col] ORDER BY [order_col]) AS rn\n  FROM [TableName]\n)\nSELECT * FROM CTE WHERE rn = 1;",
  "List Tables": "SELECT TABLE_NAME FROM INFORMATION_SCHEMA.TABLES WHERE TABLE_TYPE = 'BASE TABLE';",
};

const MONGO_TEMPLATES = {
  "Find All": "db.collectionName.find({})",
  "Find with Filter": 'db.collectionName.find({"field": "value"})',
  "Find with Sort": 'db.collectionName.find({}).sort({"field": -1})',
  "Greater Than": 'db.collectionName.find({"field": {"$gt": 100}})',
  "Insert One": 'db.collectionName.insertOne({"field": "value"})',
  "Update Many": 'db.collectionName.updateMany({"status": "pending"}, {"$set": {"status": "active"}})',
  "Delete Many": 'db.collectionName.deleteMany({"status": "inactive"})',
  "Aggregate Group": 'db.collectionName.aggregate([{"$group": {"_id": "$field", "count": {"$sum": 1}}}])',
  "Full Pipeline": 'db.collectionName.aggregate([\n  {"$match": {"status": "active"}},\n  {"$group": {"_id": "$category", "total": {"$sum": "$amount"}}},\n  {"$sort": {"total": -1}},\n  {"$limit": 10}\n])',
};

// Isolated Save Query Dialog to prevent input lag
const SaveQueryDialog = React.memo(({ open, onOpenChange, onSave, aiPrompt, disabled }) => {
  const [localName, setLocalName] = useState("");
  const [localDesc, setLocalDesc] = useState("");
  
  // Reset local state when dialog opens
  useEffect(() => {
    if (open) {
      setLocalName("");
      setLocalDesc("");
    }
  }, [open]);

  const handleSave = () => {
    if (localName.trim()) {
      onSave(localName.trim(), localDesc.trim());
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1" disabled={disabled} data-testid="save-query-btn">
          <Save className="w-4 h-4" />Save
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save Query</DialogTitle>
          <DialogDescription>Both the AI description and technical query will be saved</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="queryName">Name *</Label>
            <Input 
              id="queryName" 
              placeholder="My Query" 
              value={localName} 
              onChange={(e) => setLocalName(e.target.value)} 
              data-testid="save-query-name-input"
              autoComplete="off"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="queryDesc">Description</Label>
            <Input 
              id="queryDesc" 
              placeholder="Optional description" 
              value={localDesc} 
              onChange={(e) => setLocalDesc(e.target.value)} 
              data-testid="save-query-desc-input"
              autoComplete="off"
            />
          </div>
          {aiPrompt && (
            <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI Prompt (will be saved)</p>
              <p className="text-sm">{aiPrompt}</p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!localName.trim()} data-testid="confirm-save-query-btn">Save Query</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
});

let tabIdCounter = 1;
const createTab = (name, query = "", aiPrompt = "") => ({
  id: tabIdCounter++,
  name,
  query,
  aiPrompt,
  result: null,
  executing: false,
  currentPage: 1,
});

const QueryEditorPage = () => {
  const { getAuthHeader, updateUserCredits } = useAuth();
  const { selectedConnection, selectConnection, connections } = useConnection();
  const navigate = useNavigate();
  const [tabs, setTabs] = useState(() => {
    try {
      const saved = localStorage.getItem("queryEditorTabs");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Restore tab IDs
          const restored = parsed.map(t => ({ ...createTab(t.name, t.query, t.aiPrompt), id: t.id }));
          tabIdCounter = Math.max(...restored.map(t => t.id)) + 1;
          return restored;
        }
      }
    } catch {}
    return [createTab("Query 1")];
  });
  const [activeTabId, setActiveTabId] = useState(() => {
    try {
      const saved = localStorage.getItem("queryEditorActiveTab");
      return saved ? Number(saved) : 1;
    } catch { return 1; }
  });
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [templateSearch, setTemplateSearch] = useState("");
  const [showTemplates, setShowTemplates] = useState(false);
  const [editingTabId, setEditingTabId] = useState(null);
  const [editingTabName, setEditingTabName] = useState("");
  const editorRef = useRef(null);
  const editInputRef = useRef(null);

  // AI sync state
  const [syncing, setSyncing] = useState(false);
  const lastEditedRef = useRef('ai');

  // Syntax correction state — visible to user
  const [corrections, setCorrections] = useState(null); // { issues: [], originalQuery: "" }
  
  // AI error correction state - track if we already tried auto-fix
  const [autoFixAttempted, setAutoFixAttempted] = useState(false);
  const [autoFixing, setAutoFixing] = useState(false);

  // Saved queries for dropdown
  const [savedQueries, setSavedQueries] = useState([]);
  const [loadingSavedQueries, setLoadingSavedQueries] = useState(false);

  const activeTab = tabs.find(t => t.id === activeTabId) || tabs[0];

  const updateTab = useCallback((tabId, updates) => {
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, ...updates } : t));
  }, []);

  // Fetch saved queries when connection changes
  const fetchSavedQueries = useCallback(async () => {
    if (!selectedConnection) { setSavedQueries([]); return; }
    setLoadingSavedQueries(true);
    try {
      const response = await axios.get(`${API_URL}/query/saved?connection_id=${selectedConnection.id}`, {
        headers: getAuthHeader(), withCredentials: true
      });
      setSavedQueries(response.data);
    } catch (error) {
      console.error("Failed to fetch saved queries:", error);
    } finally {
      setLoadingSavedQueries(false);
    }
  }, [selectedConnection, getAuthHeader]);

  useEffect(() => { fetchSavedQueries(); }, [selectedConnection]);

  // Load a saved query into the active tab
  const loadSavedQuery = (sq) => {
    updateTab(activeTabId, {
      name: sq.name,
      query: sq.query,
      aiPrompt: sq.ai_prompt || ""
    });
    toast.success(`Loaded "${sq.name}"`);
  };

  // Persist tabs to localStorage
  useEffect(() => {
    const tabsToSave = tabs.map(t => ({ id: t.id, name: t.name, query: t.query, aiPrompt: t.aiPrompt }));
    localStorage.setItem("queryEditorTabs", JSON.stringify(tabsToSave));
    localStorage.setItem("queryEditorActiveTab", String(activeTabId));
  }, [tabs, activeTabId]);

  const addNewTab = () => {
    const newTab = createTab(`Query ${tabIdCounter}`);
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTab.id);
  };

  const closeTab = (tabId, e) => {
    e?.stopPropagation();
    if (tabs.length <= 1) return;
    const idx = tabs.findIndex(t => t.id === tabId);
    const newTabs = tabs.filter(t => t.id !== tabId);
    if (activeTabId === tabId) {
      setActiveTabId(newTabs[Math.min(idx, newTabs.length - 1)].id);
    }
    setTabs(newTabs);
  };

  const startRenameTab = (tabId, name, e) => {
    e?.stopPropagation();
    setEditingTabId(tabId);
    setEditingTabName(name);
  };

  const finishRenameTab = () => {
    if (editingTabId && editingTabName.trim()) {
      updateTab(editingTabId, { name: editingTabName.trim() });
    }
    setEditingTabId(null);
    setEditingTabName("");
  };

  // Monaco editor mount
  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
      handleExecute();
    });

    monaco.languages.registerCompletionItemProvider('sql', {
      provideCompletionItems: () => ({
        suggestions: ['SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'IN', 'LIKE', 'BETWEEN',
          'ORDER BY', 'GROUP BY', 'HAVING', 'TOP', 'DISTINCT',
          'INSERT INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE FROM',
          'INNER JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'ON', 'AS',
          'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
          'WITH', 'UNION', 'OVER', 'PARTITION BY', 'ROW_NUMBER'
        ].map(kw => ({ label: kw, kind: monaco.languages.CompletionItemKind.Keyword, insertText: kw, detail: 'SQL' }))
      })
    });

    monaco.languages.registerCompletionItemProvider('javascript', {
      provideCompletionItems: () => ({
        suggestions: ['find()', 'findOne()', 'insertOne()', 'updateOne()', 'updateMany()',
          'deleteOne()', 'deleteMany()', 'aggregate()', 'countDocuments()',
          '$match', '$group', '$sort', '$limit', '$project', '$lookup', '$unwind',
          '$set', '$unset', '$inc', '$push', '$pull', '$eq', '$ne', '$gt', '$lt', '$in'
        ].map(item => ({
          label: item,
          kind: item.startsWith('$') ? monaco.languages.CompletionItemKind.Operator : monaco.languages.CompletionItemKind.Method,
          insertText: item.startsWith('$') ? `"${item}"` : item,
          detail: item.startsWith('$') ? 'Operator' : 'Method'
        }))
      })
    });
  };

  // Pending query from saved queries page
  const pendingQueryRef = useRef(null);
  const initialLoadDone = useRef(false);

  useEffect(() => {
    const pendingQueryStr = sessionStorage.getItem('pendingQuery');
    if (pendingQueryStr) {
      try {
        const parsed = JSON.parse(pendingQueryStr);
        pendingQueryRef.current = parsed;
        if (parsed.query) {
          const updates = { query: parsed.query, aiPrompt: parsed.ai_prompt || "" };
          if (parsed.name) updates.name = parsed.name;
          updateTab(activeTabId, updates);
          initialLoadDone.current = true;
        }
        sessionStorage.removeItem('pendingQuery');
      } catch (e) {
        console.error('Failed to parse pending query:', e);
      }
    }
  }, []);

  useEffect(() => {
    if (pendingQueryRef.current?.connId && connections.length > 0) {
      const conn = connections.find(c => c.id === pendingQueryRef.current.connId);
      if (conn) selectConnection(conn);
      pendingQueryRef.current = null;
    }
  }, [connections, selectConnection]);

  useEffect(() => {
    if (initialLoadDone.current) return;
    if (selectedConnection && !activeTab.query) {
      const defaultQuery = selectedConnection.db_type === 'mssql'
        ? SQL_TEMPLATES["SELECT Top"]
        : MONGO_TEMPLATES["Find All"];
      updateTab(activeTabId, { query: defaultQuery });
    }
  }, [selectedConnection, activeTabId]);

  // Validate and fix query syntax — returns the corrected query
  const validateQuery = async (query) => {
    if (!selectedConnection || !query?.trim()) return query;
    try {
      const valRes = await axios.post(`${API_URL}/query/ai-validate`, {
        connection_id: selectedConnection.id,
        query: query.trim()
      }, { headers: getAuthHeader() });

      if (valRes.data.fixed && valRes.data.query !== query.trim()) {
        const correctedQuery = valRes.data.query;
        const issues = valRes.data.issues || [];
        // Store corrections so the user can see them
        setCorrections({
          issues,
          originalQuery: query.trim(),
          correctedQuery
        });
        updateTab(activeTabId, { query: correctedQuery });
        return correctedQuery;
      }
    } catch {
      // Validation unavailable — proceed with original
    }
    return query;
  };

  // AI-powered error correction for failed queries
  const attemptAIErrorCorrection = async (query, errorMessage) => {
    if (!selectedConnection) return null;
    
    try {
      const response = await axios.post(`${API_URL}/query/ai-fix-error`, {
        connection_id: selectedConnection.id,
        query: query,
        error_message: errorMessage
      }, { headers: getAuthHeader() });
      
      if (response.data.fixed_query && response.data.fixed_query !== query) {
        return {
          fixedQuery: response.data.fixed_query,
          explanation: response.data.explanation || "AI corrected the query based on the error."
        };
      }
    } catch (err) {
      console.error("AI error correction failed:", err);
    }
    return null;
  };

  const handleExecute = async (isRetry = false) => {
    if (!selectedConnection) { toast.error("Select a connection"); return; }
    if (!isRetry) {
      setCorrections(null); // Clear previous corrections
      setAutoFixAttempted(false); // Reset auto-fix tracking for new execution
    }

    // Step 1: Sync AI ↔ Query based on last edited field
    let queryToRun = activeTab.query?.trim() || "";
    const aiText = activeTab.aiPrompt?.trim() || "";

    if (!isRetry && lastEditedRef.current === 'ai' && aiText) {
      // AI was edited last — generate query from it
      updateTab(activeTabId, { executing: true, result: null, currentPage: 1 });
      try {
        const genRes = await axios.post(`${API_URL}/query/ai-generate`, {
          connection_id: selectedConnection.id,
          natural_language: aiText
        }, { headers: getAuthHeader() });
        if (genRes.data.query) {
          queryToRun = genRes.data.query;
          updateTab(activeTabId, { query: queryToRun });
        }
      } catch {
        // Continue with whatever query exists
      }
    } else if (!isRetry && lastEditedRef.current === 'query' && queryToRun && !aiText) {
      // Query edited, no AI text — generate AI description in background
      axios.post(`${API_URL}/query/ai-describe`, {
        connection_id: selectedConnection.id,
        query: queryToRun
      }, { headers: getAuthHeader() }).then(res => {
        if (res.data.description) updateTab(activeTabId, { aiPrompt: res.data.description });
      }).catch(() => {});
    }

    if (!queryToRun) { toast.error("Enter a query or AI description"); updateTab(activeTabId, { executing: false }); return; }

    // Step 2: Validate and fix syntax (visible corrections)
    if (!activeTab.executing) updateTab(activeTabId, { executing: true, result: null, currentPage: 1 });
    if (!isRetry) {
      queryToRun = await validateQuery(queryToRun);
    }

    // Step 3: Execute
    try {
      const response = await axios.post(`${API_URL}/query/execute`, {
        connection_id: selectedConnection.id,
        query: queryToRun
      }, { headers: getAuthHeader() });
      
      // Check if execution returned an error (but API call succeeded)
      if (!response.data.success && response.data.error && !autoFixAttempted && !isRetry) {
        // Attempt AI error correction once
        setAutoFixAttempted(true);
        setAutoFixing(true);
        toast.info("Query failed. Attempting AI correction...");
        
        const correction = await attemptAIErrorCorrection(queryToRun, response.data.error);
        setAutoFixing(false);
        
        if (correction) {
          toast.success("AI corrected the query. Retrying...");
          updateTab(activeTabId, { query: correction.fixedQuery });
          setCorrections({
            issues: [correction.explanation],
            originalQuery: queryToRun,
            correctedQuery: correction.fixedQuery
          });
          // Retry with corrected query
          setTimeout(() => handleExecute(true), 100);
          return;
        }
      }
      
      updateTab(activeTabId, { result: response.data, executing: false });
      if (response.data.success) {
        toast.success(`Query executed in ${response.data.execution_time_ms.toFixed(2)}ms`);
        // Update credits in sidebar if returned
        if (response.data.credits_remaining !== undefined) {
          updateUserCredits(response.data.credits_remaining);
        }
      } else {
        toast.error("Query failed");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Query execution failed";
      
      // Attempt AI correction for API errors too (if not already attempted)
      if (!autoFixAttempted && !isRetry) {
        setAutoFixAttempted(true);
        setAutoFixing(true);
        toast.info("Query failed. Attempting AI correction...");
        
        const correction = await attemptAIErrorCorrection(queryToRun, errorMsg);
        setAutoFixing(false);
        
        if (correction) {
          toast.success("AI corrected the query. Retrying...");
          updateTab(activeTabId, { query: correction.fixedQuery });
          setCorrections({
            issues: [correction.explanation],
            originalQuery: queryToRun,
            correctedQuery: correction.fixedQuery
          });
          // Retry with corrected query
          setTimeout(() => handleExecute(true), 100);
          return;
        }
      }
      
      updateTab(activeTabId, {
        result: { success: false, error: errorMsg },
        executing: false
      });
      toast.error("Query execution failed");
    }
  };

  // Single smart sync: syncs + validates
  const handleSync = async () => {
    if (!selectedConnection) { toast.error("Select a connection first"); return; }
    const direction = lastEditedRef.current;
    setSyncing(true);
    setCorrections(null);
    try {
      if (direction === 'ai') {
        // AI → Query: generate then validate
        if (!activeTab.aiPrompt?.trim()) { toast.error("Enter an AI description first"); setSyncing(false); return; }
        const response = await axios.post(`${API_URL}/query/ai-generate`, {
          connection_id: selectedConnection.id,
          natural_language: activeTab.aiPrompt.trim()
        }, { headers: getAuthHeader() });
        if (response.data.query) {
          let generatedQuery = response.data.query;
          updateTab(activeTabId, { query: generatedQuery });
          // Validate the generated query
          await validateQuery(generatedQuery);
          toast.success("Query generated and validated");
        }
      } else {
        // Query → AI: validate first, then describe
        if (!activeTab.query?.trim()) { toast.error("Enter a query first"); setSyncing(false); return; }
        const validatedQuery = await validateQuery(activeTab.query.trim());
        const response = await axios.post(`${API_URL}/query/ai-describe`, {
          connection_id: selectedConnection.id,
          query: validatedQuery
        }, { headers: getAuthHeader() });
        if (response.data.description) {
          updateTab(activeTabId, { aiPrompt: response.data.description });
          toast.success("Query validated and AI description generated");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveQuery = async (name, description) => {
    if (!name || !selectedConnection) return;
    try {
      await axios.post(`${API_URL}/query/saved`, {
        name: name,
        connection_id: selectedConnection.id,
        query: activeTab.query,
        description: description || "",
        ai_prompt: activeTab.aiPrompt || ""
      }, { headers: getAuthHeader() });
      // Update tab name to match the saved query name
      updateTab(activeTabId, { name: name });
      toast.success("Query saved");
      setSaveDialogOpen(false);
      fetchSavedQueries(); // Refresh the saved queries dropdown
    } catch (error) {
      toast.error("Failed to save query");
    }
  };

  const handleExportCSV = () => {
    if (!activeTab.result?.rows?.length) return;
    setExporting(true);
    try {
      const { columns, rows } = activeTab.result;
      const esc = (v) => {
        if (v == null) return '';
        const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csv = [columns.map(esc).join(','), ...rows.map(r => columns.map(c => esc(r[c])).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
      a.download = `query_results_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Exported ${rows.length} rows to CSV`);
    } finally { setExporting(false); }
  };

  const handleExportJSON = () => {
    if (!activeTab.result?.rows?.length) return;
    const blob = new Blob([JSON.stringify(activeTab.result.rows, null, 2)], { type: 'application/json' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `query_results_${new Date().toISOString().slice(0, 10)}.json`; a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${activeTab.result.rows.length} rows to JSON`);
  };

  const handleCopyResults = () => {
    if (!activeTab.result?.rows) return;
    navigator.clipboard.writeText(JSON.stringify(activeTab.result.rows, null, 2));
    toast.success("Copied");
  };

  const insertTemplate = (template) => {
    updateTab(activeTabId, { query: template });
    setShowTemplates(false);
    editorRef.current?.focus();
  };

  // Pagination
  const result = activeTab.result;
  const totalRows = result?.rows?.length || 0;
  const totalPages = Math.ceil(totalRows / ROWS_PER_PAGE);
  const currentPage = activeTab.currentPage || 1;
  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const endIndex = Math.min(startIndex + ROWS_PER_PAGE, totalRows);
  const paginatedRows = result?.rows?.slice(startIndex, endIndex) || [];
  const goToPage = (page) => updateTab(activeTabId, { currentPage: Math.max(1, Math.min(page, totalPages)) });

  // Cell editing
  const handleCellClick = useCallback((rowIndex, colName, value) => {
    setEditingCell({ rowIndex: startIndex + rowIndex, colName });
    setEditValue(typeof value === 'object' ? JSON.stringify(value) : String(value ?? ''));
    setTimeout(() => editInputRef.current?.focus(), 0);
  }, [startIndex]);

  const handleCellSave = useCallback(() => {
    if (!editingCell || !activeTab.result) return;
    const updatedRows = [...activeTab.result.rows];
    let newValue = editValue;
    try { if (editValue.startsWith('{') || editValue.startsWith('[')) newValue = JSON.parse(editValue); } catch {}
    updatedRows[editingCell.rowIndex] = { ...updatedRows[editingCell.rowIndex], [editingCell.colName]: newValue };
    updateTab(activeTabId, { result: { ...activeTab.result, rows: updatedRows } });
    setEditingCell(null); setEditValue("");
    toast.info("Cell updated locally.");
  }, [editingCell, editValue, activeTab, activeTabId, updateTab]);

  const handleCellCancel = useCallback(() => { setEditingCell(null); setEditValue(""); }, []);
  const handleCellKeyDown = useCallback((e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleCellSave(); }
    else if (e.key === 'Escape') handleCellCancel();
  }, [handleCellSave, handleCellCancel]);

  const templates = selectedConnection?.db_type === 'mssql' ? SQL_TEMPLATES : MONGO_TEMPLATES;
  const isMSSQL = selectedConnection?.db_type === 'mssql';
  const filteredTemplates = Object.entries(templates).filter(([name]) => name.toLowerCase().includes(templateSearch.toLowerCase()));
  const popularTemplates = Object.entries(templates).slice(0, 8);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="h-14 border-b border-border bg-card/50 backdrop-blur-sm flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          {selectedConnection ? (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-lg">
              {selectedConnection.db_type === 'mongodb' ? <Database className="w-4 h-4 text-green-500" /> : <Server className="w-4 h-4 text-blue-500" />}
              <span className="font-medium">{selectedConnection.name}</span>
              <span className="text-xs text-muted-foreground">({isMSSQL ? 'SQL' : 'MongoDB'})</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground">
              <AlertCircle className="w-4 h-4" /><span>Select a connection from the sidebar</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Saved Query Selector */}
          {selectedConnection && savedQueries.length > 0 && (
            <Select onValueChange={(val) => {
              const sq = savedQueries.find(q => q.id === val);
              if (sq) loadSavedQuery(sq);
            }}>
              <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="load-saved-query-select">
                <Bookmark className="w-3.5 h-3.5 mr-1 text-muted-foreground" />
                <SelectValue placeholder="Load saved query..." />
              </SelectTrigger>
              <SelectContent>
                {savedQueries.map(sq => (
                  <SelectItem key={sq.id} value={sq.id}>
                    <span className="truncate">{sq.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <SaveQueryDialog 
            open={saveDialogOpen} 
            onOpenChange={setSaveDialogOpen}
            onSave={handleSaveQuery}
            aiPrompt={activeTab.aiPrompt}
            disabled={!activeTab.query?.trim() || !selectedConnection}
          />
          <Button onClick={handleExecute} disabled={activeTab.executing || syncing || autoFixing || (!activeTab.query?.trim() && !activeTab.aiPrompt?.trim()) || !selectedConnection} className="gap-1" data-testid="execute-query-btn">
            {(activeTab.executing || autoFixing) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Execute
          </Button>
        </div>
      </header>

      {/* Tab Bar */}
      <div className="h-10 border-b border-border bg-muted/30 flex items-center px-1 gap-0.5 overflow-x-auto" data-testid="query-tabs-bar">
        {tabs.map(tab => (
          <div key={tab.id} onClick={() => setActiveTabId(tab.id)}
            className={`group flex items-center gap-1 px-3 py-1.5 text-xs rounded-t-md cursor-pointer transition-colors min-w-[100px] max-w-[200px] border border-b-0 ${
              tab.id === activeTabId ? 'bg-card text-foreground border-border' : 'bg-transparent text-muted-foreground border-transparent hover:bg-muted/50'
            }`} data-testid={`query-tab-${tab.id}`}>
            {tab.executing && <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" />}
            {tab.result?.success === true && !tab.executing && <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />}
            {tab.result?.success === false && !tab.executing && <AlertCircle className="w-3 h-3 text-destructive flex-shrink-0" />}
            {editingTabId === tab.id ? (
              <input className="bg-transparent text-xs w-full outline-none border-b border-primary" value={editingTabName}
                onChange={(e) => setEditingTabName(e.target.value)}
                onBlur={finishRenameTab} onKeyDown={(e) => { if (e.key === 'Enter') finishRenameTab(); if (e.key === 'Escape') setEditingTabId(null); }}
                autoFocus onClick={(e) => e.stopPropagation()} />
            ) : (
              <span className="truncate flex-1" onDoubleClick={(e) => startRenameTab(tab.id, tab.name, e)} title={`Double-click to rename`}>{tab.name}</span>
            )}
            {tabs.length > 1 && (
              <button onClick={(e) => closeTab(tab.id, e)} className="opacity-0 group-hover:opacity-100 hover:text-destructive transition-opacity flex-shrink-0" data-testid={`close-tab-${tab.id}`}>
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
        <button onClick={addNewTab} className="flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 flex-shrink-0" title="New tab" data-testid="add-query-tab-btn">
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Main Content */}
      <ResizablePanelGroup direction="vertical" className="flex-1">
        {/* Editor Area */}
        <ResizablePanel defaultSize={45} minSize={20}>
          <div className="h-full flex flex-col">
            {/* Templates Row */}
            <div className="px-4 py-1.5 border-b border-border bg-muted/30 flex items-center gap-2">
              <FileCode className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <div className="flex flex-wrap gap-1 flex-1">
                {popularTemplates.map(([name, template]) => (
                  <Button key={name} variant="ghost" size="sm" className="h-6 px-2 text-xs hover:bg-primary/20" onClick={() => insertTemplate(template)} data-testid={`template-btn-${name.toLowerCase().replace(/\s+/g, '-')}`}>
                    {name}
                  </Button>
                ))}
              </div>
              <Button variant="outline" size="sm" className="h-6 gap-1 text-xs" onClick={() => setShowTemplates(!showTemplates)} data-testid="show-all-templates-btn">
                <Search className="w-3 h-3" />{showTemplates ? 'Hide' : 'All'}
              </Button>
              <span className="text-xs text-muted-foreground flex items-center gap-1"><Info className="w-3 h-3" />Ctrl+Enter</span>
            </div>

            {showTemplates && (
              <div className="px-4 py-2 border-b border-border bg-card">
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search templates..." value={templateSearch} onChange={(e) => setTemplateSearch(e.target.value)} className="pl-9 h-8 text-sm" />
                </div>
                <div className="max-h-36 overflow-y-auto grid grid-cols-4 gap-1">
                  {filteredTemplates.map(([name, template]) => (
                    <Button key={name} variant="ghost" size="sm" className="h-7 px-2 text-xs justify-start hover:bg-primary/20" onClick={() => { insertTemplate(template); setTemplateSearch(""); }}>{name}</Button>
                  ))}
                </div>
              </div>
            )}

            {/* AI Prompt + Sync — always visible */}
            <div className="px-4 py-2 border-b border-border bg-gradient-to-r from-primary/5 to-transparent">
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-primary mt-2 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <Textarea
                    value={activeTab.aiPrompt || ""}
                    onChange={(e) => { updateTab(activeTabId, { aiPrompt: e.target.value }); lastEditedRef.current = 'ai'; }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSync(); } }}
                    placeholder="Describe what data you need in plain English..."
                    className="min-h-[36px] max-h-[80px] text-sm bg-card/50 border-primary/20 focus:border-primary resize-none"
                    disabled={syncing}
                    rows={1}
                    data-testid="ai-query-input"
                  />
                </div>
                <Button
                  size="sm"
                  className="h-9 gap-1.5 text-xs mt-0.5"
                  onClick={handleSync}
                  disabled={syncing || !selectedConnection || (!activeTab.aiPrompt?.trim() && !activeTab.query?.trim())}
                  data-testid="sync-btn"
                  title={lastEditedRef.current === 'ai' ? 'Generate query from AI description' : 'Generate AI description from query'}
                >
                  {syncing ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <ArrowRightLeft className="w-3.5 h-3.5" />
                  )}
                  {syncing ? "Syncing..." : "Sync"}
                </Button>
              </div>
            </div>

            {/* Syntax Corrections Banner */}
            {corrections && (
              <div className="px-4 py-2 border-b border-amber-500/30 bg-amber-500/5" data-testid="corrections-banner">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-amber-500 mb-1">Syntax corrections applied:</p>
                    <ul className="text-xs text-muted-foreground space-y-0.5">
                      {corrections.issues.map((issue, i) => (
                        <li key={i} className="flex items-start gap-1.5">
                          <CheckCircle2 className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{issue}</span>
                        </li>
                      ))}
                    </ul>
                    {corrections.originalQuery !== corrections.correctedQuery && (
                      <details className="mt-1.5">
                        <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">Show original query</summary>
                        <pre className="mt-1 text-xs text-muted-foreground/60 bg-muted/30 p-2 rounded overflow-x-auto font-mono">{corrections.originalQuery}</pre>
                      </details>
                    )}
                  </div>
                  <button
                    onClick={() => setCorrections(null)}
                    className="text-muted-foreground hover:text-foreground flex-shrink-0"
                    data-testid="dismiss-corrections-btn"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            {/* Monaco Editor */}
            <div className="flex-1" data-testid="query-editor-container">
              <Editor
                height="100%"
                language={isMSSQL ? "sql" : "javascript"}
                theme="vs-dark"
                value={activeTab.query}
                onChange={(value) => { updateTab(activeTabId, { query: value || "" }); lastEditedRef.current = 'query'; if (corrections) setCorrections(null); }}
                onMount={handleEditorDidMount}
                options={{
                  minimap: { enabled: false }, fontSize: 14,
                  fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
                  lineNumbers: "on", scrollBeyondLastLine: false, automaticLayout: true,
                  tabSize: 2, wordWrap: "on", padding: { top: 12, bottom: 12 },
                  suggestOnTriggerCharacters: true, quickSuggestions: true,
                  renderLineHighlight: "all", cursorBlinking: "smooth", smoothScrolling: true,
                }}
              />
            </div>
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* Results Panel */}
        <ResizablePanel defaultSize={55} minSize={20}>
          <div className="h-full flex flex-col">
            <div className="h-12 border-b border-border bg-card/50 flex items-center justify-between px-4">
              <div className="flex items-center gap-3">
                {result ? (
                  result.success ? (
                    <>
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span className="text-sm">{totalRows} row{totalRows !== 1 ? 's' : ''}</span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{result.execution_time_ms?.toFixed(2)}ms</span>
                    </>
                  ) : (
                    <><AlertCircle className="w-4 h-4 text-destructive" /><span className="text-sm text-destructive">Query failed</span></>
                  )
                ) : (
                  <span className="text-sm text-muted-foreground">Results will appear here</span>
                )}
              </div>
              {result?.success && result.rows?.length > 0 && (
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={handleCopyResults} title="Copy" data-testid="copy-results-btn"><Copy className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={handleExportCSV} className="gap-1" disabled={exporting} data-testid="export-results-csv-btn">
                    {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}CSV ({totalRows})
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleExportJSON} className="gap-1" data-testid="export-results-json-btn"><Download className="w-4 h-4" />JSON</Button>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-auto">
              {!result ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-8">
                  <Database className="w-12 h-12 text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">Execute a query to see results</p>
                </div>
              ) : autoFixing ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-8">
                  <Loader2 className="w-12 h-12 text-primary mb-4 animate-spin" />
                  <p className="text-primary font-medium">AI is analyzing the error...</p>
                  <p className="text-muted-foreground text-sm mt-1">Attempting to fix the query automatically</p>
                </div>
              ) : result.error ? (
                <div className="p-4" data-testid="query-error-display">
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg overflow-hidden">
                    {/* Error Header */}
                    <div className="px-4 py-3 bg-destructive/15 border-b border-destructive/20 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
                      <span className="font-semibold text-destructive">Query Execution Error</span>
                      {autoFixAttempted && (
                        <span className="ml-auto text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded">
                          AI auto-fix attempted
                        </span>
                      )}
                    </div>
                    {/* Error Body */}
                    <div className="p-4 space-y-3">
                      {/* Parse and format the error message */}
                      {(() => {
                        const errorText = result.error;
                        // Try to extract structured parts from common error formats
                        const lines = errorText.split(/\n|(?:\. (?=[A-Z]))/);
                        const mainError = lines[0];
                        const details = lines.slice(1).filter(l => l.trim());
                        
                        return (
                          <>
                            <div className="font-mono text-sm text-destructive bg-destructive/5 p-3 rounded border border-destructive/10">
                              {mainError}
                            </div>
                            {details.length > 0 && (
                              <div className="space-y-1">
                                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Details</p>
                                <ul className="space-y-1">
                                  {details.map((detail, i) => (
                                    <li key={i} className="text-xs text-muted-foreground font-mono pl-3 border-l-2 border-muted">
                                      {detail.trim()}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </>
                        );
                      })()}
                      
                      {/* Suggestions */}
                      <div className="pt-2 border-t border-border">
                        <p className="text-xs text-muted-foreground">
                          <span className="font-medium">Tip:</span> Check your query syntax, table/column names, and data types.
                          {!autoFixAttempted && " The AI will attempt to auto-fix errors on the next run."}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : result.rows?.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-8">
                  <CheckCircle2 className="w-12 h-12 text-green-500/30 mb-4" />
                  <p className="text-muted-foreground">Query executed - No rows returned</p>
                </div>
              ) : (
                <div className="w-full h-full overflow-x-auto overflow-y-auto" data-testid="results-scroll-container">
                  <table className="data-table w-max min-w-full border-collapse">
                    <thead className="sticky top-0 bg-card z-10">
                      <tr className="border-b border-border">
                        <th className="w-12 text-center px-4 py-2 text-xs font-medium text-muted-foreground sticky left-0 bg-card">#</th>
                        {result.columns?.map(col => (
                          <th key={col} className="whitespace-nowrap font-medium px-4 py-2 text-xs text-left min-w-[150px] border-r border-border last:border-r-0">{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRows.map((row, idx) => (
                        <tr key={idx} className="border-b border-border hover:bg-muted/30" data-testid={`result-row-${idx}`}>
                          <td className="text-center text-xs text-muted-foreground px-4 py-2 sticky left-0 bg-card">{startIndex + idx + 1}</td>
                          {result.columns?.map(col => {
                            const isEditing = editingCell?.rowIndex === (startIndex + idx) && editingCell?.colName === col;
                            const cellValue = row[col];
                            const displayValue = typeof cellValue === 'object' ? JSON.stringify(cellValue) : String(cellValue ?? '');
                            return (
                              <td key={col} className="font-mono text-xs p-0 min-w-[150px] border-r border-border last:border-r-0">
                                {isEditing ? (
                                  <div className="flex items-center gap-1 p-1">
                                    <Input ref={editInputRef} value={editValue} onChange={(e) => setEditValue(e.target.value)} onKeyDown={handleCellKeyDown} className="h-7 text-xs font-mono" />
                                    <Button size="icon" variant="ghost" className="h-6 w-6 text-green-500" onClick={handleCellSave}><Check className="w-3 h-3" /></Button>
                                    <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={handleCellCancel}><X className="w-3 h-3" /></Button>
                                  </div>
                                ) : (
                                  <div className="px-4 py-2 cursor-pointer hover:bg-muted/50 transition-colors group flex items-center justify-between gap-2" onClick={() => handleCellClick(idx, col, cellValue)} title="Click to edit" data-testid={`cell-${idx}-${col}`}>
                                    <span className="truncate max-w-[250px]">{displayValue}</span>
                                    <Edit2 className="w-3 h-3 opacity-0 group-hover:opacity-50 flex-shrink-0" />
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Insufficient Credits Banner */}
            {result?.insufficient_credits && (
              <div className="px-4 py-2 border-t border-red-500/30 bg-gradient-to-r from-red-500/10 to-transparent flex items-center justify-between" data-testid="no-credits-banner">
                <div className="flex items-center gap-2">
                  <Crown className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-red-500">
                    Insufficient credits — <strong>Buy credits to continue running queries</strong>
                  </span>
                </div>
                <Button size="sm" className="gap-1.5 bg-primary hover:bg-primary/90" onClick={() => navigate('/subscribe')} data-testid="buy-credits-btn">
                  <Zap className="w-3.5 h-3.5" />
                  Buy Credits
                </Button>
              </div>
            )}

            {/* Credits remaining */}
            {result?.credits_remaining !== undefined && !result?.insufficient_credits && (
              <div className="px-4 py-1 border-t border-border bg-muted/20 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  Credits remaining: <strong className="text-foreground">{result.credits_remaining}</strong>
                  {result.credit_cost ? ` (${result.credit_cost} per query)` : ''}
                </span>
              </div>
            )}

            {result?.success && totalRows > 0 && (
              <footer className="h-12 border-t border-border bg-card/50 flex items-center justify-between px-4">
                <div className="text-sm text-muted-foreground">Showing {startIndex + 1} - {endIndex} of {totalRows}</div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" onClick={() => goToPage(1)} disabled={currentPage === 1} data-testid="first-page-btn"><ChevronsLeft className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} data-testid="prev-page-btn"><ChevronLeft className="w-4 h-4" /></Button>
                  <span className="px-3 text-sm">Page {currentPage} of {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} data-testid="next-page-btn"><ChevronRight className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages} data-testid="last-page-btn"><ChevronsRight className="w-4 h-4" /></Button>
                </div>
              </footer>
            )}
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
};

export default QueryEditorPage;
